import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  CircularProgress,
  Checkbox,
} from "@material-ui/core";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";
import CloseIcon from "@material-ui/icons/Close";
import {
  presentFormatedData,
  dateFormated,
} from "../../functionalComponents/dataFormatFunctions";
import StyledDialog from "../../../../../common/models/Dialog";
import StyledButton from "../../../../../common/models/Button";
import WarningIcon from "@material-ui/icons/Warning";
import { useDispatch } from "react-redux";
import {
  DESTROY_BACKDROP,
  OPEN_BACKDROP,
} from "../../../../containers/actions/types";
import BackDropLoading from "../../models/BackDrop";
import EditPlannedAllocatedQuantityDialog from "./EditPlannedAllocatedQuantityDialog";

// An allocated batch is only fit for consumption once it has actually
// reached the CMO (i.e. sits at Special Stocks with Vendor). Any other
// storage location means the stock hasn't been shipped there yet.
const isNotSpecialStock = (storageLocation) =>
  !!(storageLocation && String(storageLocation).trim() !== "");

// An allocated batch past its expiry date is not fit for consumption.
const isPastExpiry = (expiryDate) => {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) return false;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return expiry < todayStart;
};

const useStyles = makeStyles((theme) => ({
  closeButton: {
    fontSize: "1rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#FF0000",
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #d0d0d0",
    borderRadius: "8px",
    marginBottom: "24px",
    overflow: "auto",
    maxWidth: "100%",
    width: 0,
    minWidth: "100%",
  },
  table: {
    minWidth: 650,
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: "#F5F5F5",
    borderBottom: "1px solid #d0d0d0",
    padding: "12px 16px",
  },
  tableCell: {
    padding: "12px 16px",
    borderBottom: "1px solid #d0d0d0",
  },
  changeAllocationButton: {
    backgroundColor: "transparent",
    color: "#000",
    border: "1px solid #000",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  confirmButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
  successDialog: {
    textAlign: "center",
    padding: "40px 20px",
  },
  successIcon: {
    fontSize: "80px",
    color: "#39811D",
    marginBottom: "20px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
  },
  successMessage: {
    fontSize: "16px",
    color: "#666",
    marginBottom: "8px",
  },
  okButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
  },
  updatedHeader: {
    // "& .MuiTableCell-root":{
    //   padding:"0px"
    // },
    fontWeight: 700,
    fontSize: 13.6,
  },
  updatedSubHeader: {
    fontSize: 13.6,
    padding: "0px",
  },
  // Greyed, non-clickable Deplete Instruction indicator (mirrors LotAllocationTable.jsx)
  customCheckbox: {
    opacity: 0.6,
  },
  stickyCol1: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: "#fff",
  },
  stickyCol2: {
    position: "sticky",
    left: 202,
    zIndex: 2,
    backgroundColor: "#fff",
  },
  stickyCol3: {
    position: "sticky",
    left: 323,
    zIndex: 2,
    backgroundColor: "#fff",
  },
  stickyHeaderCol1: {
    position: "sticky",
    left: 0,
    zIndex: 3,
  },
  stickyHeaderCol2: {
    position: "sticky",
    left: 202,
    zIndex: 3,
  },
  stickyHeaderCol3: {
    position: "sticky",
    left: 323,
    zIndex: 3,
  },
}));

const ReviewPopUp = ({ open, handleClose, ...props }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  // const [loading, setLoading] = useState(false);
  // const [consumptionData, setConsumptionData] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [disableConfirmBtn, setDisableBtn] = useState(false);

  const [lotAllocationData, setLotAllocationData] = useState([]);
  const [lotAllocationRes, setLotAllocationRes] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedLotData, setSelectedLotData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editSuccessDialogOpen, setEditSuccessDialogOpen] = useState(false);
  const [editSuccessMessage, setEditSuccessMessage] = useState("");

  // Extract data from props
  const {
    fgMaterialCode,
    fgMaterialName,
    vendorBatchNumber,
    batchUOM,
    batchSize,
    scheduleAgreementNumber,
    schrockenSLID,
    vendorName,
    batchRowData,
    onRefreshBatchTableData,
    // schrockenDeliveryScheduleBatchID,
    // bomConsumptionAddedIndicator,
  } = props;

  // Consumption gate: every allocated batch must be at Special Stocks with
  // Vendor (i.e. shipped to the CMO) and not past its expiry date.
  const reviewErrors = useMemo(() => {
    const errors = [];
    if (!Array.isArray(lotAllocationData)) return errors;

    lotAllocationData.forEach((component) => {
      (component?.lotNumberAllocationDetails || []).forEach((lot) => {
        if (!lot?.lotNumber || !lot?.plannedAllocatedQuantity) return;

        if (isNotSpecialStock(lot?.storageLocation)) {
          errors.push(
            `Allocated batch ${lot.lotNumber} is not at Special Stocks with Vendor storage location (currently ${lot.storageLocation}).`
          );
        }

        if (isPastExpiry(lot?.expiryDate)) {
          const formattedExpiry = dateFormated(
            lot.expiryDate,
            "DD-MMM-YYYY",
            ""
          ).toUpperCase();
          errors.push(
            `Allocated batch ${lot.lotNumber} has expired on ${formattedExpiry}.`
          );
        }
      });
    });

    return errors;
  }, [lotAllocationData]);

  // Fetch component allocation data
  useEffect(() => {
    if (open && schrockenSLID) {
      fetchLotAllocationData();
    }
  }, [open, schrockenSLID]);

  useEffect(() => {
    if (
      lotAllocationData &&
      Array.isArray(lotAllocationData) &&
      lotAllocationData.length > 0
    ) {
      setSelectedLotData(lotAllocationData[0]);
    }
  }, [lotAllocationData]);

  const fetchLotAllocationData = () => {
    setTableLoading(true);
    let url = "";
    url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationForPOLineNewDirect/${schrockenSLID}`;
    // if (bomConsumptionAddedIndicator) {
    //   // Get batchSize from batchDetails
    //   url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationPlannedAndActualNew/${schrockenDeliveryScheduleBatchID}/${batchSize}`;
    // } else {
    //   url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationForPOLineNewDirect/${schrockenSLID}`;
    // }

    axios
      .get(url)
      .then((res) => {
        if (res.data.status) {
          if (res.data.data.componentAllocationDataList.length > 0) {
            setLotAllocationData(res.data.data.componentAllocationDataList);
            setLotAllocationRes(res.data.data);
            // let btn = res.data.data.componentAllocationDataList.every(
            //   ({ lotNumberAllocationDetails }) => {
            //     if (
            //       Array.isArray(lotNumberAllocationDetails) &&
            //       lotNumberAllocationDetails.length > 0
            //     ) {
            //       return lotNumberAllocationDetails.every(
            //         ({ lotNumber, plannedAllocatedQuantity }) => {
            //           return !lotNumber || !plannedAllocatedQuantity;
            //         }
            //       );
            //     }
            //   }
            // );
            res.data.data.componentAllocationDataList.forEach((val, i) => {
              if (val?.lotNumberAllocationDetails.length > 0) {
                val?.lotNumberAllocationDetails.forEach((lot) => {
                  if (!lot?.lotNumber || !lot?.plannedAllocatedQuantity) {
                    setDisableBtn(true);
                  }
                });
              } else {
                setDisableBtn(true);
              }
            });
            // setLotAllocationRes(res.data.data);
          } else {
            setLotAllocationData([]);
            setLotAllocationRes({});
            setError(false);
            setDisableBtn(true);
          }
        } else {
          setError(true); // If API response is not successful
        }
      })
      .catch((err) => {
        setError(true);
      })
      .finally(() => {
        setTableLoading(false);
      });
  };

  const handleConfirmConsumption = async () => {
    setIsSubmitting(true);
    try {
      dispatch({
        type: OPEN_BACKDROP,
        backdropText: "Please wait....",
      });
      let payload = {
        bowls: [],
        poNumber: scheduleAgreementNumber,
        batchNumber: vendorBatchNumber,
      };

      // lotAllocationData.forEach((val, index) => {
      //   if (val?.lotNumberAllocationDetails.length > 0) {
      //     payload = {
      //       ...payload,
      //       bowls: payload.bowls.push({
      //         ID: index + 1,
      //         bowl: {
      //           value: String.fromCharCode(65 + index),
      //         },
      //         rows: val?.lotNumberAllocationDetails.map((lot, i) => {
      //           if (lot?.lotNumber && lot?.plannedAllocatedQuantity) {
      //             return {
      //               consumed_material_number: {
      //                 value: val?.componentAllocationDetails?.componentCode,
      //               },
      //               actual_material_quantity_consumed: {
      //                 value: lot?.plannedAllocatedQuantity,
      //               },
      //               consumption_batch_number: {
      //                 value: lot?.lotNumber,
      //               },
      //               consumed_material_uom: {
      //                 value: val?.componentAllocationDetails?.uom,
      //               },
      //               consumption_batch_number_sponsor: {
      //                 value: lot?.lotNumber,
      //               },
      //               row_id: i + 1,
      //             };
      //           }
      //         }),
      //       }),
      //     };
      //   }
      // });
      const bowls = [];

      lotAllocationData.forEach((val, index) => {
        if (val?.lotNumberAllocationDetails?.length > 0) {
          bowls.push({
            ID: index + 1,
            bowl: {
              value: String.fromCharCode(65 + index),
            },
            rows: val.lotNumberAllocationDetails
              .filter((lot) => lot?.lotNumber && lot?.plannedAllocatedQuantity)
              .map((lot, i) => ({
                consumed_material_number: {
                  value: val?.componentAllocationDetails?.componentCode,
                },
                actual_material_quantity_consumed: {
                  value: lot.plannedAllocatedQuantity,
                },
                consumption_batch_number: {
                  value: lot.lotNumber,
                },
                consumed_material_uom: {
                  value: val?.componentAllocationDetails?.uom,
                },
                consumption_batch_number_sponsor: {
                  value: lot.lotNumber,
                },
                row_id: i + 1,
              })),
          });
        }
      });

      payload = {
        ...payload,
        bowls,
        poNumber: scheduleAgreementNumber,
        batchNumber: vendorBatchNumber,
      };

      // setTimeout(() => {
      //   const response = {
      //     data: {
      //       status: false,
      //     },
      //     status: false,
      //   };
      //   if (response.data && response.data.status) {
      //     setShowSuccessDialog(true);
      //   } else {
      //     setSaveFailed(true);
      //   }
      //   dispatch({
      //     type: DESTROY_BACKDROP,
      //   });
      // }, 5000);

      let url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/formConsumptionFromReview`;
      axios
        .post(url, payload)
        .then((res) => {
          if (res.data.status) {
            setShowSuccessDialog(true);
          } else {
            setSaveFailed(true);
          }
        })
        .catch((err) => {
          setSaveFailed(true);
        })
        .finally(() => {
          dispatch({
            type: DESTROY_BACKDROP,
          });
        });
    } catch (error) {
      console.error("Error confirming consumption:", error);
      setSaveFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    handleClose();
    onRefreshBatchTableData();
  };

  const handleChangeAllocation = () => {
    setOpenDialog(true);
  };

  // Function to close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    // setSelectedLotData(null);
  };

  const handleLotAllocationStatus = (
    type,
    message = "Component Allocation updated successfully"
  ) => {
    if (type === "success") {
      setEditSuccessMessage(message);
      setEditSuccessDialogOpen(true);
    } else if (type === "failed") {
      setSaveFailed(true);
    }
  };

  return (
    <React.Fragment>
      {/* Main Review Consumption Dialog */}
      {/* <BackDropLoading /> */}
      <Dialog
        open={open && !showSuccessDialog}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 3,
            backgroundColor: "#fafafa",
          },
        }}
        style={{ maxHeight: "90vh" }}
      >
        <DialogContent
          // sx={{ overflowY: "auto" }}
          style={{
            position: "relative",
            padding: "24px 40px 32px 40px",
            overflowX: "hidden",
          }}
        >
          {/* Close Button */}
          <Grid
            container
            justifyContent="flex-end"
            style={{ marginBottom: "16px" }}
          >
            <Typography
              className={classes.closeButton}
              variant="body2"
              onClick={handleClose}
            >
              Cancel{" "}
              <CloseIcon style={{ marginLeft: "4px" }} fontSize="large" />
            </Typography>
          </Grid>

          <Typography
            variant="h5"
            style={{
              textAlign: "center",
              padding: "0.5rem",
              fontWeight: 500,
              fontSize: "1.8rem",
            }}
          >
            Review Consumption
          </Typography>

          <Typography
            variant="body2"
            style={{
              textAlign: "center",
              fontSize: "1.2rem",
              margin: "1rem",
            }}
          >
            Confirm that consumption details match allocation
          </Typography>

          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: "#f2f8fb",
              border: "1px solid #ccc",
              borderRadius: "4px",
              // padding: "3px",
            }}
          >
            <Table
              className={classes.tablepadding}
              style={{ backgroundColor: "#f2f8fb" }}
            >
              <TableHead>
                <TableRow style={{ maxHeight: "2rem" }}>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      CMO
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      Product Code / Description
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      Batch Number
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      Batch Size
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      UOM
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedHeader}>
                      PO Number
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow
                  style={{
                    backgroundColor: "#f2f8fb",
                    border: "none",
                    maxHeight: "2rem",
                  }}
                >
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(vendorName)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(fgMaterialCode)} /{" "}
                      {presentFormatedData(fgMaterialName)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(vendorBatchNumber)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(batchSize)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(batchUOM)}
                    </Typography>
                  </TableCell>
                  <TableCell style={{ border: "none" }}>
                    <Typography className={classes.updatedSubHeader}>
                      {presentFormatedData(scheduleAgreementNumber)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer
            style={{
              marginTop: "1rem",
              minHeight: "auto",
              maxHeight: "400px",
            }}
            component={Paper}
            className={classes.tableContainer}
          >
            <Table stickyHeader className="default-table-styles">
              {/* Table Head */}
              <TableHead>
                {/* Main header row */}
                <TableRow>
                  <TableCell
                    style={{ minWidth: "170px" }}
                    align="left"
                    className={`${classes.headerCell} ${classes.stickyHeaderCol1}`}
                  >
                    BMS Material Code / Description
                  </TableCell>
                  <TableCell
                    style={{ minWidth: "90px" }}
                    align="right"
                    className={`${classes.headerCell} ${classes.stickyHeaderCol2}`}
                  >
                    BoM Quantity
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap", minWidth: "90px" }}
                    align="left"
                    className={`${classes.headerCell} ${classes.stickyHeaderCol3}`}
                  >
                    UOM
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="left"
                    className={classes.headerCell}
                  >
                    Storage Location ID
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="left"
                    className={classes.headerCell}
                  >
                    BMS Material Batch Numbers
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="left"
                    className={classes.headerCell}
                  >
                    Date of Manufacturing
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="left"
                    className={classes.headerCell}
                  >
                    Expiry Date
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="right"
                    className={classes.headerCell}
                  >
                    Planned Allocated Qty
                  </TableCell>
                  <TableCell
                    style={{ whiteSpace: "nowrap" }}
                    align="center"
                    className={classes.headerCell}
                  >
                    Deplete Instruction
                  </TableCell>
                </TableRow>
              </TableHead>
              {tableLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9}>
                      <div style={{ textAlign: "center" }}>
                        <CircularProgress color="primary" thickness={7} />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : error ? (
                <TableBody>
                  <TableRow
                    style={{
                      border: "2px solid rgb(255,0,0)",
                      backgroundColor: "rgb(255,240,240)",
                      Opacity: 1,
                      textAlign: "center",
                      padding: "0.8rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    <TableCell
                      style={{ padding: "10px" }}
                      colSpan={9}
                      align="center"
                    >
                      Something went wrong. Please try again later.
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {lotAllocationData?.length > 0 ? (
                    lotAllocationData.map((component, index) => (
                      <React.Fragment key={index}>
                        {/* Case 1: Display lot allocation data if available */}
                        {component?.lotNumberAllocationDetails.length > 0 ? (
                          component?.lotNumberAllocationDetails.map(
                            (lot, lotIndex) => (
                              <TableRow key={lotIndex}>
                                {lotIndex === 0 && (
                                  <TableCell
                                    align="left"
                                    rowSpan={
                                      component.lotNumberAllocationDetails
                                        .length
                                    }
                                    className={classes.stickyCol1}
                                  >
                                    <div>
                                      {presentFormatedData(
                                        component?.componentAllocationDetails
                                          ?.componentCode,
                                        ""
                                      )}{" "}
                                      {"/"}
                                    </div>
                                    <div>
                                      {presentFormatedData(
                                        component?.componentAllocationDetails
                                          ?.componentName,
                                        ""
                                      )}
                                    </div>
                                  </TableCell>
                                )}

                                {lotIndex === 0 && (
                                  <TableCell
                                    align="right"
                                    rowSpan={
                                      component.lotNumberAllocationDetails
                                        .length
                                    }
                                    className={classes.stickyCol2}
                                  >
                                    {component?.componentAllocationDetails
                                      ?.componentQuantity !== undefined &&
                                    component?.componentAllocationDetails
                                      ?.componentQuantity !== null
                                      ? Number(
                                          component?.componentAllocationDetails
                                            ?.componentQuantity
                                        ).toFixed(3)
                                      : ""}
                                  </TableCell>
                                )}

                                {lotIndex === 0 && (
                                  <TableCell
                                    align="left"
                                    rowSpan={
                                      component.lotNumberAllocationDetails
                                        .length
                                    }
                                    className={classes.stickyCol3}
                                  >
                                    {presentFormatedData(
                                      component?.componentAllocationDetails
                                        ?.uom,
                                      ""
                                    )}
                                  </TableCell>
                                )}

                                <TableCell
                                  align="left"
                                  style={
                                    isNotSpecialStock(lot?.storageLocation)
                                      ? { color: "#c62828", fontWeight: 700 }
                                      : undefined
                                  }
                                >
                                  {(() => {
                                    const sl = lot?.storageLocation;
                                    if (sl === undefined) return "";
                                    if (sl && String(sl).trim() !== "")
                                      return sl;
                                    return "Special Stocks with Vendor";
                                  })()}
                                </TableCell>

                                <TableCell
                                  style={{
                                    whiteSpace: "nowrap",
                                  }}
                                  align="left"
                                >
                                  {lot?.lotNumber || ""}
                                </TableCell>

                                <TableCell align="left">
                                  {(() => {
                                    const direct = lot?.dateOfManufacturing;
                                    if (direct) {
                                      return dateFormated(
                                        direct,
                                        "DD-MMM-YYYY",
                                        ""
                                      ).toUpperCase();
                                    }
                                    const inv = component?.inventoryDetails?.find(
                                      (i) =>
                                        i?.inventoryBatchNumber ===
                                        lot?.lotNumber
                                    );
                                    return inv?.dateOfManufacturing
                                      ? dateFormated(
                                          inv.dateOfManufacturing,
                                          "DD-MMM-YYYY",
                                          ""
                                        ).toUpperCase()
                                      : "";
                                  })()}
                                </TableCell>

                                <TableCell
                                  align="left"
                                  style={
                                    isPastExpiry(lot?.expiryDate)
                                      ? { color: "#c62828", fontWeight: 700 }
                                      : undefined
                                  }
                                >
                                  {dateFormated(
                                    lot?.expiryDate,
                                    "DD-MMM-YYYY",
                                    ""
                                  ).toUpperCase()}
                                </TableCell>

                                <TableCell>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: "8px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        minWidth: "40px",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {(() => {
                                        if (lot?.plannedAllocatedQuantity === null || lot?.plannedAllocatedQuantity === undefined || lot?.plannedAllocatedQuantity === "") return "";
                                        const numericValue = Number(lot.plannedAllocatedQuantity);
                                        return isNaN(numericValue) ? lot.plannedAllocatedQuantity : numericValue.toFixed(3);
                                      })()}
                                    </div>
                                  </div>
                                </TableCell>

                                <TableCell align="center">
                                  {lot?.isDepletedBatch && (
                                    <Checkbox
                                      checked
                                      disabled
                                      className={classes.customCheckbox}
                                      size="small"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          )
                        ) : (
                          // Case 3: Fallback - Display component data with "NA" values when no lot or inventory data available
                          <TableRow>
                            <TableCell align="left">
                              <div>
                                {presentFormatedData(
                                  component?.componentAllocationDetails
                                    ?.componentCode,
                                  ""
                                )}
                                {"/"}
                              </div>
                              <div>
                                {presentFormatedData(
                                  component?.componentAllocationDetails
                                    ?.componentName,
                                  ""
                                )}
                              </div>
                            </TableCell>

                            <TableCell align="right">
                              {component?.componentAllocationDetails
                                ?.componentQuantity !== undefined &&
                              component?.componentAllocationDetails
                                ?.componentQuantity !== null
                                ? Number(
                                    component?.componentAllocationDetails
                                      ?.componentQuantity
                                  ).toFixed(3)
                                : ""}
                            </TableCell>
                            <TableCell
                              style={{ paddingLeft: "3%" }}
                              align="left"
                            >
                              {presentFormatedData(
                                component?.componentAllocationDetails?.uom,
                                ""
                              )}
                            </TableCell>
                            <TableCell align="left">{""}</TableCell>
                            <TableCell
                              style={{ whiteSpace: "nowrap" }}
                              align="left"
                            >
                              {""}
                            </TableCell>
                            <TableCell align="left">{""}</TableCell>
                            <TableCell align="left">{""}</TableCell>
                            <TableCell
                              style={{ whiteSpace: "nowrap" }}
                              align="left"
                            >
                              {""}
                            </TableCell>
                            <TableCell align="center" />
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              )}
            </Table>
          </TableContainer>

          {reviewErrors.length > 0 && (
            <div
              style={{
                marginTop: "1rem",
                padding: "12px 16px",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
                backgroundColor: "#fdecea",
              }}
            >
              {reviewErrors.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    color: "#c62828",
                    fontSize: "0.875rem",
                    marginBottom: i === reviewErrors.length - 1 ? 0 : "6px",
                  }}
                >
                  <WarningIcon style={{ fontSize: "1rem", marginTop: "2px" }} />
                  <Typography>{msg}</Typography>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions
          style={{
            justifyContent: "space-between",
            paddingTop: "24px",
            paddingBottom: "24px",
            paddingLeft: "40px",
            paddingRight: "40px",
          }}
        >
          <Button
            variant="outlined"
            className={classes.changeAllocationButton}
            onClick={handleChangeAllocation}
          >
            Change Allocation
          </Button>
          <Button
            variant="contained"
            className={classes.confirmButton}
            onClick={handleConfirmConsumption}
            disabled={
              isSubmitting ||
              tableLoading ||
              disableConfirmBtn ||
              reviewErrors.length > 0
            }
            endIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? "Confirming..." : "Confirm Consumption"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} fullWidth>
        <StyledDialog
          icons={true}
          handleClose={() => {
            handleSuccessClose();
          }}
          title={"Consumption confirmed successfully"}
          subtitle={"Process flow and batch status change is initiated"}
        />
      </Dialog>

      <Dialog
        open={saveFailed}
        onClose={() => {
          setSaveFailed(false);
        }}
        fullWidth
      >
        <DialogContent style={{ padding: "2rem" }}>
          <Grid container justifyContent="flex-end">
            <Typography
              variant="body2"
              style={{
                fontSize: "1rem",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => {
                setSaveFailed(false);
              }}
            >
              Close <CloseIcon fontSize="large" />
            </Typography>
            <Grid
              item
              xs={12}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <WarningIcon
                style={{
                  fontSize: "5rem",
                  color: "#F00",
                }}
              />
            </Grid>
          </Grid>
          <div
            style={{
              display: "flex",
              justifyContent: "center", // Horizontally center the CircularProgress
              alignItems: "center", // Vertically center the CircularProgress
              height: "80px", // Optional: Set a height to vertically center the loader
              border: "2px solid #FF0000",
              backgroundColor: "#FFF0F0",
              // width: "100%",
            }}
          >
            <Typography style={{ textAlign: "center" }}>
              Something went wrong. Please try again.
            </Typography>
          </div>

          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
              padding: "0.5rem",
            }}
          >
            <StyledButton
              style={{ paddingLeft: "3rem", paddingRight: "3rem" }}
              onClick={() => {
                setSaveFailed(false);
              }}
            >
              Ok
            </StyledButton>
          </Grid>
        </DialogContent>
      </Dialog>

      <Dialog open={editSuccessDialogOpen} fullWidth>
        <StyledDialog
          icons={true}
          handleClose={() => {
            setEditSuccessDialogOpen(false);
            setEditSuccessMessage("");
            fetchLotAllocationData();
            setDisableBtn(false);
          }}
          title={
            editSuccessMessage || "Component Allocation updated successfully"
          }
        />
      </Dialog>

      <Dialog
        open={openDialog}
        // onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <EditPlannedAllocatedQuantityDialog
          lotData={selectedLotData}
          lotAllocationRes={lotAllocationRes}
          lotAllocationData={lotAllocationData}
          // rmPmCode={rmPmCode}
          onClose={handleCloseDialog}
          poData={batchRowData}
          schrockenSLID={schrockenSLID}
          vendorMosymphonyID={batchRowData.vendorMosymphonyID}
          handleClose={handleLotAllocationStatus}
        />
      </Dialog>
    </React.Fragment>
  );
};

export default ReviewPopUp;
